import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ClipboardCheck, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

export default function CoursePlayer() {
    const { aid } = useParams();
    const nav = useNavigate();
    const [a, setA] = useState(null);
    const [pct, setPct] = useState(0);
    const [lp, setLp] = useState(null);
    const videoRef = useRef(null);
    const lastRangeRef = useRef({ start: null, last: 0 });

    async function load() {
        const r = await api.get(`/assignments/${aid}`);
        setA(r.data);
        // Start if not started
        if (!r.data.progress) {
            const s = await api.post(`/assignments/${aid}/start`);
            setLp(s.data);
            setPct(s.data.watch_percentage || 0);
        } else {
            setLp(r.data.progress);
            setPct(r.data.progress.watch_percentage || 0);
        }
    }
    useEffect(() => { load(); }, [aid]);

    async function flushRange() {
        const rec = lastRangeRef.current;
        if (rec.start == null || rec.last <= rec.start) return;
        const dur = videoRef.current?.duration || a?.video_render?.duration_s || 1;
        try {
            const r = await api.post(`/learner-progress/${lp.id}/watch`, {
                added_range: [rec.start, rec.last],
                duration_s: dur,
            });
            setPct(r.data.watch_percentage);
        } catch (e) { console.warn("watch update fail", e); }
        lastRangeRef.current = { start: null, last: 0 };
    }

    useEffect(() => {
        if (!lp) return;
        const iv = setInterval(flushRange, 5000);
        return () => { flushRange(); clearInterval(iv); };
    }, [lp?.id]);

    function onTimeUpdate(e) {
        const t = e.target.currentTime;
        if (lastRangeRef.current.start == null) {
            lastRangeRef.current.start = t;
        }
        lastRangeRef.current.last = t;
    }
    function onSeeked(e) {
        // Break the range on seek
        flushRange();
        lastRangeRef.current.start = e.target.currentTime;
        lastRangeRef.current.last = e.target.currentTime;
    }

    if (!a) return <div className="p-8 text-neutral-500">Loading…</div>;
    const passed = a?.progress?.passed;
    const retakeAllowed = a?.retake_allowed !== false;
    const attempts = a?.progress?.attempts || 0;
    const retakeLimit = a?.retake_count_limit;
    const retakeLimitReached = retakeLimit != null && attempts >= retakeLimit;
    const canTakeQuiz = pct >= 95 && !passed;
    const canRetake = passed && retakeAllowed && !retakeLimitReached;

    return (
        <div className="min-h-screen bg-neutral-950 text-neutral-100">
            <header className="border-b border-neutral-800">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Button size="sm" variant="ghost" onClick={() => nav("/learn")} data-testid="btn-back-dashboard"><ArrowLeft size={14} className="mr-1"/>Back</Button>
                    <div className="font-semibold">{a.module_name}</div>
                    <Button size="sm" variant="ghost" onClick={() => nav("/ai/ask")} data-testid="btn-ask-ai"><MessageSquare size={14} className="mr-1"/>Ask AI</Button>
                </div>
            </header>
            <main className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-12 gap-6">
                <aside className="col-span-3">
                    <h3 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-3">Chapters</h3>
                    <div className="space-y-1">
                        {(a.chapters || []).map((c, i) => (
                            <div key={c.id} className="px-3 py-2 rounded border border-neutral-800" data-testid={`chapter-${i}`}>
                                <div className="text-sm">{c.name}</div>
                                <div className="text-xs text-neutral-500">{c.summary?.slice(0, 60) || ""}</div>
                            </div>
                        ))}
                    </div>
                </aside>
                <section className="col-span-9">
                    {a.video_render?.signed_url ? (
                        <div className="bg-black rounded overflow-hidden">
                            <video
                                ref={videoRef}
                                src={a.video_render.signed_url}
                                controls
                                onTimeUpdate={onTimeUpdate}
                                onSeeked={onSeeked}
                                className="w-full aspect-video"
                                data-testid="course-video"
                            >
                                {a.video_render.captions_url && (
                                    <track default kind="captions" srcLang="en" src={a.video_render.captions_url}/>
                                )}
                            </video>
                        </div>
                    ) : (
                        <Card><CardContent className="py-16 text-center text-neutral-400">Film not available yet.</CardContent></Card>
                    )}
                    <div className="mt-4 flex items-center gap-4">
                        <div className="flex-1">
                            <div className="h-2 rounded bg-neutral-800 overflow-hidden">
                                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} data-testid="watch-progress-bar"/>
                            </div>
                            <div className="text-xs text-neutral-500 mt-1.5" data-testid="watch-percentage">
                                {pct.toFixed(0)}% watched
                                {attempts > 0 && <span className="ml-3">· {attempts} attempt{attempts !== 1 ? "s" : ""}</span>}
                                {retakeLimit != null && <span className="ml-3">· {Math.max(0, retakeLimit - attempts)} retake{retakeLimit - attempts !== 1 ? "s" : ""} left</span>}
                            </div>
                        </div>
                        {passed && (
                            <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600" data-testid="completed-badge">
                                Passed · {a.progress.best_score?.toFixed(0)}%
                            </Badge>
                        )}
                        {canRetake && (
                            <Button onClick={() => nav(`/learn/modules/${aid}/quiz`)} variant="outline" data-testid="btn-retake-quiz">
                                <ClipboardCheck size={16} className="mr-2"/>Retake Knowledge Check
                            </Button>
                        )}
                        {passed && !retakeAllowed && (
                            <span className="text-xs text-neutral-500" data-testid="retake-not-allowed">Retakes not allowed for this assignment.</span>
                        )}
                        {passed && retakeAllowed && retakeLimitReached && (
                            <span className="text-xs text-neutral-500" data-testid="retake-limit-reached">Retake limit reached.</span>
                        )}
                        {!passed && canTakeQuiz && (
                            <Button onClick={() => nav(`/learn/modules/${aid}/quiz`)} data-testid="btn-take-quiz">
                                <ClipboardCheck size={16} className="mr-2"/>Take Knowledge Check
                            </Button>
                        )}
                        {!passed && !canTakeQuiz && (
                            <Button disabled data-testid="btn-take-quiz-disabled">Take Knowledge Check (watch 95%)</Button>
                        )}
                    </div>
                </section>
            </main>
        </div>
    );
}
